import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Wrench } from 'lucide-react';
import { useDatabase } from '../../context/DatabaseContext';
import { combinedToolsApi as api } from '../../services/api';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function substituteBaseTemplate(template, panelName, drop) {
  if (!template) return '';
  return template.replace('{PANEL}', panelName || '').replace('{DROP}', drop || '');
}

function triggerDownload(content, filename) {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Datasheet column definitions ────────────────────────────────────────────

const COLUMNS = [
  { key: 'tagId',       label: 'Tag ID',      width: 200 },
  { key: 'description', label: 'Description', width: 280 },
  { key: 'panel',       label: 'Panel',       width: 130 },
  { key: 'ioType',      label: 'IO Type',     width:  80 },
  { key: 'drop',        label: 'Drop',        width:  70 },
  { key: 'slot',        label: 'Slot',        width:  70 },
  { key: 'channel',     label: 'Channel',     width:  80 },
  { key: 'address',     label: 'Address',     width: 110 },
  { key: 'min',         label: 'Min',         width:  80 },
  { key: 'max',         label: 'Max',         width:  80 },
  { key: 'eu',          label: 'EU',          width:  80 },
  { key: 'source',      label: 'Source',      width: 110 },
];

// ─── Platform Selection Modal ─────────────────────────────────────────────────

function PlatformModal({ panels, platforms, onGenerate, onClose }) {
  const [selections, setSelections] = useState(() => {
    const init = {};
    panels.forEach(p => {
      const def = platforms[0] || null;
      const baseOverride = def && !def.isVfd
        ? substituteBaseTemplate(def.defaultBaseTemplate, p.panelName, p.drop)
        : '';
      init[p.panelName] = {
        platformId: def?.id ?? 0,
        baseOverride,
        aoiCode: def?.defaultAoiCode ?? 'E01',
        isVfd: def?.isVfd ?? false,
        isLocal: p.drop?.toUpperCase() === 'L',
      };
    });
    return init;
  });

  const [generating, setGenerating] = useState(false);

  const handlePlatformChange = (panelName, platformId) => {
    const plat = platforms.find(p => p.id === platformId);
    const panel = panels.find(p => p.panelName === panelName);
    const isLocal = panel?.drop?.toUpperCase() === 'L';
    setSelections(prev => ({
      ...prev,
      [panelName]: {
        ...prev[panelName],
        platformId,
        isVfd: plat?.isVfd ?? false,
        isLocal,
        baseOverride: plat && !plat.isVfd && !isLocal
          ? substituteBaseTemplate(plat.defaultBaseTemplate, panelName, panel?.drop)
          : prev[panelName].baseOverride,
        aoiCode: plat?.defaultAoiCode ?? prev[panelName].aoiCode,
      },
    }));
  };

  const handleGenerate = async () => {
    setGenerating(true);
    const panelSelections = panels.map(p => {
      const sel = selections[p.panelName];
      return {
        panelName: p.panelName,
        drop: p.drop,
        platformId: sel.platformId,
        baseOverride: sel.isLocal ? 'Local' : sel.baseOverride,
        aoiCode: sel.aoiCode,
      };
    });
    await onGenerate(panelSelections);
    setGenerating(false);
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={modalHeaderStyle}>
          <h3 style={{ margin: 0, fontSize: 16, color: 'var(--color-primary)' }}>Configure Panels</h3>
          <button onClick={onClose} style={modalCloseStyle}>×</button>
        </div>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--color-gray-500)' }}>
          Select a platform for each panel. Edit the base address if it differs from the default.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--color-gray-50)' }}>
              <th style={thStyle}>Panel</th>
              <th style={thStyle}>Drop</th>
              <th style={thStyle}>Platform</th>
              <th style={thStyle}>Base / AOI Code</th>
            </tr>
          </thead>
          <tbody>
            {panels.map(p => {
              const sel = selections[p.panelName];
              const plat = platforms.find(pl => pl.id === sel.platformId);
              return (
                <tr key={p.panelName} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                  <td style={tdStyle}>{p.panelName}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{p.drop || '—'}</td>
                  <td style={tdStyle}>
                    <select
                      value={sel.platformId}
                      onChange={e => handlePlatformChange(p.panelName, parseInt(e.target.value))}
                      style={inputStyle}
                    >
                      {platforms.map(pl => (
                        <option key={pl.id} value={pl.id}>{pl.displayName}</option>
                      ))}
                    </select>
                  </td>
                  <td style={tdStyle}>
                    {sel.isLocal ? (
                      <span style={{ color: 'var(--color-gray-400)', fontStyle: 'italic' }}>Local (auto)</span>
                    ) : plat?.isVfd ? (
                      <input
                        type="text"
                        value={sel.aoiCode}
                        onChange={e => setSelections(prev => ({
                          ...prev,
                          [p.panelName]: { ...prev[p.panelName], aoiCode: e.target.value },
                        }))}
                        placeholder="AOI Code (e.g. E01)"
                        style={inputStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={sel.baseOverride}
                        onChange={e => setSelections(prev => ({
                          ...prev,
                          [p.panelName]: { ...prev[p.panelName], baseOverride: e.target.value },
                        }))}
                        placeholder="Base address"
                        style={inputStyle}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={modalFooterStyle}>
          <button className="ct-btn ct-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="ct-btn ct-btn-primary" onClick={handleGenerate} disabled={generating}>
            {generating ? 'Generating...' : 'Generate'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Unresolved Tags Modal ────────────────────────────────────────────────────

function UnresolvedModal({ tags, onSubmit, onClose }) {
  const [values, setValues] = useState(() => {
    const init = {};
    tags.forEach(t => { init[t.tagId] = { min: '', max: '' }; });
    return init;
  });

  const handleSubmit = () => {
    const overrides = tags.map(t => ({
      tagId: t.tagId,
      min: parseFloat(values[t.tagId]?.min) || null,
      max: parseFloat(values[t.tagId]?.max) || null,
    }));
    onSubmit(overrides);
  };

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, maxWidth: 560 }}>
        <div style={modalHeaderStyle}>
          <h3 style={{ margin: 0, fontSize: 16, color: 'var(--color-primary)' }}>Engineering Limits Required</h3>
          <button onClick={onClose} style={modalCloseStyle}>×</button>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--color-gray-500)' }}>
          The following tags have no Min/Max in PMD. Enter values to continue.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--color-gray-50)' }}>
              <th style={thStyle}>Tag</th>
              <th style={thStyle}>Description</th>
              <th style={thStyle}>EU</th>
              <th style={thStyle}>Min</th>
              <th style={thStyle}>Max</th>
            </tr>
          </thead>
          <tbody>
            {tags.map(t => (
              <tr key={t.tagId} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                <td style={tdStyle}>{t.tagId}</td>
                <td style={tdStyle}>{t.description}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{t.eu || '—'}</td>
                <td style={tdStyle}>
                  <input type="number" value={values[t.tagId]?.min || ''} style={{ width: 70, ...inputStyle }}
                    onChange={e => setValues(prev => ({ ...prev, [t.tagId]: { ...prev[t.tagId], min: e.target.value } }))} />
                </td>
                <td style={tdStyle}>
                  <input type="number" value={values[t.tagId]?.max || ''} style={{ width: 70, ...inputStyle }}
                    onChange={e => setValues(prev => ({ ...prev, [t.tagId]: { ...prev[t.tagId], max: e.target.value } }))} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={modalFooterStyle}>
          <button className="ct-btn ct-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="ct-btn ct-btn-primary" onClick={handleSubmit}>Generate</button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal shared styles ──────────────────────────────────────────────────────

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const modalStyle = {
  background: '#fff', borderRadius: 8, padding: 24,
  boxShadow: '0 4px 24px rgba(0,0,0,0.18)', width: '90%',
  maxWidth: 680, maxHeight: '80vh', overflow: 'auto',
};
const modalHeaderStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 };
const modalCloseStyle = { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--color-gray-400)', lineHeight: 1 };
const modalFooterStyle = { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 };
const thStyle = { padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--color-gray-500)', borderBottom: '1px solid var(--color-gray-200)' };
const tdStyle = { padding: '7px 10px', verticalAlign: 'middle' };
const inputStyle = { width: '100%', padding: '4px 6px', fontSize: 13, border: '1px solid var(--color-gray-300)', borderRadius: 4 };

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CombinedToolsPage() {
  const { dbSlug: slug } = useDatabase();

  // Parent + tag data
  const [parents, setParents]               = useState([]);
  const [selectedParent, setSelectedParent] = useState('');
  const [loadingParents, setLoadingParents] = useState(false);
  const [tags, setTags]                     = useState([]);
  const [loadingTags, setLoadingTags]       = useState(false);
  const [filters, setFilters]               = useState({});

  // Platform modal
  const [showPlatformModal, setShowPlatformModal] = useState(false);
  const [panels, setPanels]       = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loadingModal, setLoadingModal] = useState(false);

  // Unresolved modal
  const [showUnresolvedModal, setShowUnresolvedModal] = useState(false);
  const [unresolvedTags, setUnresolvedTags]           = useState([]);
  const [pendingPanelSelections, setPendingPanelSelections] = useState(null);

  const [error, setError] = useState('');

  // Load parents on mount
  useEffect(() => {
    if (!slug) return;
    setLoadingParents(true);
    api.getParents(slug)
      .then(res => {
        setParents(res.data);
        if (res.data.length > 0) setSelectedParent(res.data[0]);
      })
      .catch(() => setParents([]))
      .finally(() => setLoadingParents(false));
  }, [slug]);

  // Load tags when parent changes
  useEffect(() => {
    if (!slug || !selectedParent) { setTags([]); return; }
    setLoadingTags(true);
    setFilters({});
    setError('');
    api.getTags(slug, selectedParent)
      .then(res => setTags(res.data.map(t => ({ ...t, id: t.tagId }))))
      .catch(() => { setTags([]); setError('Failed to load tag data.'); })
      .finally(() => setLoadingTags(false));
  }, [slug, selectedParent]);

  // Visible tags — respects FilterableDataGrid's array-based filter format
  const visibleTags = useMemo(() =>
    tags.filter(t =>
      Object.entries(filters).every(([key, vals]) =>
        !vals || vals.length === 0 || vals.includes(String(t[key] || ''))
      )
    ),
    [tags, filters]
  );

  const handleExecute = useCallback(async () => {
    if (!selectedParent || !slug) return;
    setLoadingModal(true);
    setError('');
    try {
      // Derive panels from visible (filtered) tags — respects current filter state
      const panelMap = {};
      visibleTags.forEach(t => {
        if (t.panel && !panelMap[t.panel])
          panelMap[t.panel] = t.drop ?? null;
      });
      const derivedPanels = Object.entries(panelMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([panelName, drop]) => ({ panelName, drop }));

      const platformsRes = await api.getPlatforms(slug);
      setPanels(derivedPanels);
      setPlatforms(platformsRes.data);
      setShowPlatformModal(true);
    } catch {
      setError('Failed to load panel/platform data.');
    } finally {
      setLoadingModal(false);
    }
  }, [slug, selectedParent, visibleTags]);

  const handleGenerate = useCallback(async (panelSelections, tagOverrides = null) => {
    setShowPlatformModal(false);
    setShowUnresolvedModal(false);
    setError('');
    setPendingPanelSelections(panelSelections);

    try {
      const res = await api.generate(slug, {
        parent: selectedParent,
        panelSelections,
        tagOverrides,
        tagIds: visibleTags.map(t => t.tagId),
      });
      const data = res.data;

      if (data.unresolvedTags?.length > 0) {
        setUnresolvedTags(data.unresolvedTags);
        setShowUnresolvedModal(true);
        return;
      }

      downloadFiles(data, selectedParent);
    } catch {
      setError('Generation failed. Check the API logs.');
    }
  }, [slug, selectedParent, visibleTags]);

  const handleUnresolvedSubmit = useCallback((overrides) => {
    if (!pendingPanelSelections) return;
    handleGenerate(pendingPanelSelections, overrides);
  }, [pendingPanelSelections, handleGenerate]);

  return (
    <div className="dev-module combined-tools-page">

      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <Wrench size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            IO Buffer Generation
          </h1>
          <span className="dev-module-meta">PLC Import CSV · Input Buffer · Output Buffer</span>
        </div>
      </div>

      <div className="dev-module-content">

        <div>
          {/* Toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            {loadingParents ? (
              <span style={{ fontSize: 13, color: 'var(--color-gray-400)' }}>Loading...</span>
            ) : (
              <select
                value={selectedParent}
                onChange={e => setSelectedParent(e.target.value)}
                className="ct-parent-select"
                style={{ maxWidth: 380 }}
              >
                {parents.length === 0 && <option value="">No parents found</option>}
                {parents.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
            <button
              className="import-generate-btn"
              onClick={handleExecute}
              disabled={!selectedParent || loadingModal || tags.length === 0}
            >
              {loadingModal ? 'Loading...' : 'Execute'}
            </button>
          </div>

          {error && <div className="ct-error">{error}</div>}

          {/* Tag datasheet */}
          {loadingTags && (
            <div style={{ fontSize: 13, color: 'var(--color-gray-400)', marginTop: 8 }}>Loading tags...</div>
          )}
          {!loadingTags && tags.length > 0 && (
            <FilterableDataGrid
              data={visibleTags}
              allData={tags}
              columns={COLUMNS}
              filters={filters}
              setFilters={setFilters}
            />
          )}
        </div>

      </div>

      {/* Modals */}
      {showPlatformModal && (
        <PlatformModal
          panels={panels}
          platforms={platforms}
          onGenerate={handleGenerate}
          onClose={() => setShowPlatformModal(false)}
        />
      )}
      {showUnresolvedModal && (
        <UnresolvedModal
          tags={unresolvedTags}
          onSubmit={handleUnresolvedSubmit}
          onClose={() => setShowUnresolvedModal(false)}
        />
      )}
    </div>
  );
}

function downloadFiles(data, parent) {
  const files = [
    { content: data.fileA, filename: `${parent}_PLC_Import.csv` },
    { content: data.fileB, filename: `${parent}_InputBuffer.txt` },
    { content: data.fileC, filename: `${parent}_OutputBuffer.txt` },
  ];
  files.forEach(({ content, filename }) => triggerDownload(content, filename));
}
